<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('shipments.shipments') }}
        </span>
        <h3 class="page-title">
          {{ $t('shipments.shipmentsList') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end align-middle">
        <d-button
          theme="success"
          @click.prevent="showAddPopup = true;">
          <i class="material-icons">add</i>
          {{ $t('shipments.addNewShipmentUPS') }}
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="allDataLoaded"
      :view-name="'shipments'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0"
              style="min-width: 120px;">{{ $t('shipments.status') }}</th>
            <th
              scope="col"
              class="border-0"
              style="min-width: 100px">{{ $t('shipments.shipper') }}</th>
            <th
              scope="col"
              class="border-0"
              style="min-width: 100px">{{ $t('shipments.createdAt') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('shipments.trackingNumber') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('shipments.relatedOrder') }}</th>
            <th
              scope="col"
              class="border-0"
              style="min-width: 220px;">{{ $t('shipments.shipperData') }}</th>
            <th
              scope="col"
              class="border-0"
              style="min-width: 220px;">{{ $t('shipments.receiverData') }}</th>
            <th
              scope="col"
              class="border-0"
              style="min-width: 120px;">{{ $t('shipments.packages') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 200px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(shipment, index) of shipments"
            :key="'shipment-' + index">
            <td
              :id="'shipments-item-' + index"
              class="align-middle">
              <d-badge
                v-if="shipment.tracking_status"
                :theme="trackingStatusBadgeTheme[shipment.tracking_status]"
                class="mb-2"
                :title="$t('order.lastChange') + ': ' + shipment.tracking_status_at">
                {{ $t('shipments.trackingStatuses.' + shipment.tracking_status) }}
              </d-badge>

              <br v-if="shipment.tracking_status" />

              <d-badge
                :id="'shipments-status-' + index"
                :outline="true"
                :theme="statusBadgeTheme[shipment.status]"
                :style="shipment.error_code ? 'cursor: help;' : ''">
                {{ $t('shipments.statuses.' + shipment.status.toLowerCase()) }}
              </d-badge>

              <d-tooltip
                v-if="shipment.error_code"
                :target="'#shipments-status-' + index"
                :container="'shipments-item-' + index">
                <small>
                  <strong class="text-danger">{{ shipment.error_code }}</strong><br>
                  <span>{{ shipment.error_message }}</span>
                </small>
              </d-tooltip>
            </td>
            <td class="align-middle">
              <small>{{ shipment.shipper }}</small>
            </td>
            <td class="align-middle">
              <small>{{ shipment.created_at }}</small>
            </td>
            <td class="align-middle">
              <template v-if="shipment.shipper === 'DPD'">
                {{ shipment.tracking_number }}
              </template>
              <template v-else>
                <small v-if="shipment.packages.length">
                  <span
                    v-for="(pkg, index) of shipment.packages"
                    :key="'package-' + shipment.id + '-' + index">
                    {{ pkg.tracking_number }}<br>
                  </span>
                </small>
                <small v-else>
                  {{ $t('ui.lack') }}
                </small>
              </template>

              <small class="d-block text-muted">
                {{ $t('shipments.estimateDeliveryDate') }}:
                <template v-if="shipment.estimate_delivery_date">{{ shipment.estimate_delivery_date }}</template>
                <template v-else>{{ $t('ui.lack') }}</template>
              </small>
            </td>
            <td class="align-middle">
              <small v-if="shipment.order_id">
                <router-link :to="'/zamowienia/edycja/' + parseInt(shipment.order_id, 10)">
                  <template v-if="shipment.order_name">{{ shipment.order_name }}</template>
                  <template v-else>{{ $t('ui.lack') }}</template>
                </router-link>
              </small>
              <small v-else>
                {{ $t('shipments.noRelatedOrder') }}
              </small>
            </td>
            <td class="align-middle">
              <small>
                {{ shipment.shipper_number }}<br>
                {{ shipment.shipper_name }}
              </small>
            </td>
            <td class="align-middle">
              <small>
                {{ shipment.receiver_name }}<br>
                <template v-if="shipment.receiver_email">email: {{ shipment.receiver_email }}<br></template>
                <template v-if="shipment.receiver_phone">tel: {{ shipment.receiver_phone }}</template>
              </small>
            </td>
            <td class="align-middle">
              <small v-if="shipment.packages_count">
                {{ $t('shipments.packagesCount') }}: {{ shipment.packages_count }}<br>
              </small>
              <small v-else>
                {{ $t('ui.lack') }}
              </small>
            </td>
            <td class="align-middle">
              <d-dropdown
                v-if="shipment.status === 'INTEGRATED' && (shipment.shipper !== 'DPD' || (shipment.shipper === 'DPD' && shipment.can_generate_pod))"
                icon="cloud_download"
                theme="info"
                :disabled="downloadingLabel !== false || downloadingPod !== false"
                class="mr-2">
                <d-dropdown-item v-if="shipment.shipper !== 'DPD' && shipment.label_url">
                  <a
                    :disabled="downloadingLabel !== false"
                    @click.prevent="downloadLabel(shipment.id)"
                    :style="'display: block;' + ((downloadingLabel !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingLabel !== shipment.id">{{ $t('shipments.downloadLabelForShipment') }}</template>
                    <template v-else>
                      <div
                        class="spinner-border spinner-border-sm"
                        role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
                <d-dropdown-item v-if="shipment.can_generate_pod">
                  <a
                    :disabled="downloadingPod !== false"
                    @click.prevent="downloadPod(shipment.pod_url, shipment.id)"
                    :style="'display: block;' + ((downloadingPod !== false) ? 'pointer-events: none;' : '')">
                    <template v-if="downloadingPod !== shipment.id">{{ $t('shipments.downloadPodForShipment') }}</template>
                    <template v-else>
                      <div
                        class="spinner-border spinner-border-sm"
                        role="status"></div>
                    </template>
                  </a>
                </d-dropdown-item>
              </d-dropdown>

              <d-button
                v-if="shipment.shipper !== 'DPD'"
                theme="primary"
                @click.prevent="openViewPopup(shipment.id)"
                class="mr-2"
                :title="$t('shipments.viewShipment')">
                <i class="material-icons">visibility</i>
              </d-button>

              <d-button
                theme="danger"
                :disabled="cancellingShipment !== false || shipment.status === 'CANCELLED' || !shipment.can_cancel"
                @click.prevent="cancelShipment(shipment.id)"
                :title="$t('shipments.cancelShipment')">
                <template v-if="cancellingShipment !== shipment.id">
                  <i class="material-icons">cancel</i>
                </template>
                <template v-else>
                  <div
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                </template>
              </d-button>
            </td>
          </tr>
          <tr v-if="shipments.length === 0">
            <td
              colspan="9"
              class="text-center">
              {{ $t('shipments.noShipmentsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <shipment-add-popup
      v-if="showAddPopup"/>

    <shipment-view-popup
      v-if="showViewPopup"
      :shipmentID="selectedShipmentID" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import ShipmentAddPopup from '../components/shipments/ShipmentAddPopup.vue';
import ShipmentViewPopup from '../components/shipments/ShipmentViewPopup.vue';
import cloneDeep from 'lodash.clonedeep';
import Vue from 'vue';

export default {
  name: 'shipments',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination,
    'shipment-add-popup': ShipmentAddPopup,
    'shipment-view-popup': ShipmentViewPopup
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    },
    statusBadgeTheme () {
      return {
        NEW: 'primary',
        ERROR: 'danger',
        INTEGRATED: 'info',
        CANCELLED: 'dark',
        DELIVERED: 'success'
      };
    },
    trackingStatusBadgeTheme () {
      return {
        I: 'warning',
        D: 'success',
        X: 'danger',
        P: 'info',
        M: 'dark'
      };
    },
    statusOptions () {
      return [
        {
          text: this.$t('shipments.statuses.new'),
          value: 'NEW'
        },
        {
          text: this.$t('shipments.statuses.error'),
          value: 'ERROR'
        },
        {
          text: this.$t('shipments.statuses.integrated'),
          value: 'INTEGRATED'
        },
        {
          text: this.$t('shipments.statuses.cancelled'),
          value: 'CANCELLED'
        },
      ];
    },
    filtersConfig () {
      return [
        {
          key: 'show_all',
          apiKey: 'show_all',
          label: this.$t('ui.showAll'),
          type: 'checkbox',
          isBasic: true,
          defaultValue: false
        },
        {
          key: 'order_name',
          apiKey: 'order.order_name', // Bezpośrednie mapowanie do zagnieżdżonego klucza API
          label: this.$t('ui.orderName'),
          placeholder: this.$t('shipments.enterOrderName'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          maxWidth: '20%'
        },
        {
          key: 'tracking_number',
          apiKey: 'tracking_number',
          label: this.$t('shipments.trackingNumber'),
          placeholder: this.$t('shipments.enterTrackingNumber'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          maxWidth: '20%'
        },
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('users.customer'),
          placeholder: this.$t('shipments.enterCustomerName'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          maxWidth: '20%'
        },
        {
          key: 'status',
          apiKey: 'status',
          label: this.$t('shipments.status'),
          placeholder: this.$t('shipments.selectStatus'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.statusOptions,
          maxWidth: '20%'
        }
      ];
    }
  },
  data () {
    return {
      shipments: [],
      loaded: false,
      loadError: false,
      downloadingLabel: false,
      downloadingPod: false,
      cancellingShipment: false,
      showAddPopup: false,
      showViewPopup: false,
      selectedShipmentID: null,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.$bus.$on('shipments-reload', this.debouncedLoadFilteredData);
    this.$bus.$on('add-shipment-popup-close', this.closeShipmentAddPopup);
    this.$bus.$on('view-shipment-popup-close', this.closeShipmentViewPopup);
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      // Wyłuskujemy show_all z obiektu where, ponieważ w przypadku tego endpointu
      // API oczekuje go na zewnątrz, a nie wewnątrz klauzuli where.
      let requestShowAll = false;
      if (where.show_all) {
        requestShowAll = true;
        delete where.show_all;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/shipments/items?page=' + this.currentPage,
        listField: 'shipments',
        params: {
          show_all: requestShowAll,
          where
        }
      });
    },
    downloadLabel (shipmentID) {
      this.downloadingLabel = shipmentID;

      this.$http.post('/shipments/download-label/' + shipmentID, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('shipments.labelFilePrefix');
        link.setAttribute('download', filenamePrefix + shipmentID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingLabel = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingLabel = false;
      });
    },
    downloadPod (podUrl, shipmentID) {
      this.downloadingPod = shipmentID;

      this.$http.post(podUrl, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('shipments.podFilePrefix');
        link.setAttribute('download', filenamePrefix + shipmentID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingPod = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingPod = false;
      });
    },
    cancelShipment (shipmentID) {
      this.cancellingShipment = shipmentID;

      this.$http.post('/shipments/cancel/' + shipmentID, null).then(response => {
        if (response && response.data && response.data.success) {
          Vue.swal({
            title: this.$t('shipments.shipmentCancelled'),
            html: this.$t('shipments.shipmentCancelledDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.cancellingShipment = false;
          this.$bus.$emit('shipments-reload');
        } else {
          Vue.swal({
            title: this.$t('shipments.shipmentCancelError'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.cancellingShipment = false;
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('shipments.shipmentCancelError'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.cancellingShipment = false;
      });
    },
    closeShipmentAddPopup () {
      this.showAddPopup = false;
    },
    closeShipmentViewPopup () {
      this.showViewPopup = false;
    },
    openViewPopup (shipmentID) {
      this.selectedShipmentID = shipmentID;
      this.showViewPopup = true;
    }
  },
  beforeDestroy () {
    this.$bus.$off('shipments-reload', this.debouncedLoadFilteredData);
    this.$bus.$off('add-shipment-popup-close', this.closeShipmentAddPopup);
    this.$bus.$off('view-shipment-popup-close', this.closeShipmentViewPopup);
  }
};
</script>

<style lang="scss" scoped>
</style>
